import { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

// ✅ Register once at module level
gsap.registerPlugin(ScrollTrigger);

const steps = [
  {
    number: "01",
    title: "Discovery & Strategy",
    subtitle: "We understand your business",
    description:
      "We start by understanding your products, target audience, and business goals. Our team analyses the UK market landscape across TikTok Shop, Shopify, and eBay to create a tailored multi-platform strategy.",
    deliverables: ["Market Analysis", "Platform Strategy", "Timeline & Roadmap"],
  },
  {
    number: "02",
    title: "Setup & Launch",
    subtitle: "We build your presence",
    description:
      "Our team handles complete account creation, store setup, product listings optimisation, payment gateway integration, and UK tax compliance. We create professional, conversion-focused storefronts.",
    deliverables: ["Account Creation", "Store Design", "Product Listings"],
  },
  {
    number: "03",
    title: "Scale & Optimise",
    subtitle: "We grow your revenue",
    description:
      "Once live, we continuously optimise your listings, manage inventory, run promotional campaigns, and scale your operations. Our data-driven approach ensures sustained growth across all platforms.",
    deliverables: ["Performance Tracking", "Ad Campaigns", "Ongoing Support"],
  },
];

const BG_IMAGE = "https://ik.imagekit.io/g9mnvfpan/pattern_background.jpg?updatedAt=1780915539610";
const FLOAT_IMG = "https://ik.imagekit.io/g9mnvfpan/3-removebg-preview%20(1).png";

export default function HowWeWorkSteps() {
  const sectionRef = useRef(null);
  const triggerContainerRef = useRef(null);
  const cardsRef = useRef([]);
  const floatingImageRef = useRef(null);
  const headingRef = useRef(null);

  useEffect(() => {
    const section = sectionRef.current;
    const triggerContainer = triggerContainerRef.current;
    const heading = headingRef.current;
    if (!section || !triggerContainer) return;

    // ✅ FIX: Wrap everything in GSAP Context & MatchMedia for complete performance cleanup
    const ctx = gsap.context(() => {
      const mm = gsap.matchMedia();

      // Desktop Pinning & Card Stacking Sequence
      mm.add("(min-width: 1025px)", () => {
        if (heading) {
          gsap.to(heading, {
            backgroundPositionX: "0%",
            ease: "none",
            scrollTrigger: {
              trigger: section,
              start: "top 80%",
              end: "top 20%",
              scrub: true,
            },
          });
        }

        ScrollTrigger.create({
          trigger: section,
          start: "top bottom",
          end: "bottom top",
          onEnter: () => gsap.to(".hww-fixed-bg", { opacity: 1, duration: 0.3, overwrite: "auto" }),
          onLeave: () => gsap.to(".hww-fixed-bg", { opacity: 1, duration: 0.3, overwrite: "auto" }),
          onEnterBack: () => gsap.to(".hww-fixed-bg", { opacity: 1, duration: 0.3, overwrite: "auto" }),
          onLeaveBack: () => gsap.to(".hww-fixed-bg", { opacity: 0, duration: 0.3, overwrite: "auto" }),
        });

        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: triggerContainer,
            start: "top top",
            end: "+=250%",
            pin: true,
            scrub: 1,
            invalidateOnRefresh: true,
            fastScrollEnd: true,
          },
        });

        cardsRef.current.forEach((card, index) => {
          if (!card) return;
          // GPU accelerated entrance animation using translate3d under the hood
          tl.fromTo(
            card,
            { yPercent: 120, opacity: 0 },
            { yPercent: 0, opacity: 1, duration: 1, ease: "power2.out" },
            index === 0 ? "0" : "+=0.25"
          );
        });
      });

      // Mobile Responsive Fallback Animations
      mm.add("(max-width: 1024px)", () => {
        gsap.set(".hww-fixed-bg", { opacity: 1 });
        if (heading) gsap.set(heading, { backgroundPositionX: "0%" });

        cardsRef.current.forEach((card) => {
          if (!card) return;
          gsap.set(card, { clearProps: "all" });
          gsap.fromTo(
            card,
            { y: 30, opacity: 0 },
            {
              y: 0,
              opacity: 1,
              duration: 0.5,
              scrollTrigger: {
                trigger: card,
                start: "top 88%",
                toggleActions: "play none none none",
              },
            }
          );
        });
      });

      // Infinite compositor-friendly float effect
      gsap.fromTo(
        floatingImageRef.current,
        { y: -12 },
        { y: 12, duration: 3, ease: "sine.inOut", yoyo: true, repeat: -1 }
      );
    }, sectionRef);

    return () => ctx.revert(); // ✅ Kills all tweens and ScrollTriggers automatically on unmount
  }, []);

  return (
    <>
      <style>{`
        .hww-wrap * { box-sizing: border-box; margin: 0; padding: 0; }
        .hww-wrap {
          font-family: 'Inter', sans-serif;
          position: relative;
          background: #FFF;
          width: 100%;
          overflow: hidden;
        }
        .hww-fixed-bg-wrapper {
          position: absolute;
          top: 0; left: 0;
          width: 100%; height: 100%;
          z-index: 0;
          overflow: hidden;
          pointer-events: none;
        }
        .hww-fixed-bg {
          position: fixed;
          top: 0; left: 0;
          width: 100%; height: 100vh;
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
          opacity: 0;
          will-change: opacity;
          transform: translateZ(0);
        }
        .hww-content-layer { position: relative; width: 100%; z-index: 1; }
        
        /* ✅ Centered Header Alignment */
        .hww-header {
          width: 100%;
          max-width: 1440px;
          margin: 0 auto;
          padding: clamp(60px, 8vw, 100px) 24px 0;
          text-align: center;
          position: relative;
          z-index: 10;
        }
        .hww-header h1 {
          font-family: 'epilogue', sans-serif;
          font-size: clamp(32px, 6.5vw, 110px);
          font-weight: 900;
          text-transform: uppercase;
          letter-spacing: -0.02em;
          line-height: 1.05;
          display: inline-block;
          background: linear-gradient(to right, #F97316 50%, #E2E8F0 50%);
          background-size: 200% 100%;
          background-position-x: 100%;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .hww-scroll-engine {
          position: relative;
          width: 100%;
          height: 100vh;
          display: flex;
          align-items: center;
          z-index: 5;
          overflow: hidden;
        }
        .hww-cards-row {
          position: relative;
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          width: 100%;
          max-width: 1440px;
          margin: 0 auto;
          padding: 0 40px;
          gap: clamp(16px, 2vw, 24px);
          align-items: stretch;
          z-index: 2;
        }
        .hww-card-item {
          position: relative;
          background: #FFFFFF;
          border: 1px solid #EDE4D8;
          border-radius: 24px;
          padding: clamp(24px, 3vw, 40px) clamp(20px, 2.5vw, 28px);
          display: flex;
          flex-direction: column;
          box-shadow: 0 20px 50px rgba(30,18,4,0.05);
          will-change: transform, opacity;
          backface-visibility: hidden;
          font-family: 'epilogue', sans-serif;
          height: 100%;
        }
        .hww-corner-accent {
          position: absolute;
          width: 14px; height: 14px;
          border: 2px solid transparent;
          pointer-events: none;
          z-index: 2;
        }
        .hww-corner-tl { top: 12px; left: 12px; border-top-color: #F97316; border-left-color: #F97316; border-top-left-radius: 6px; }
        .hww-corner-tr { top: 12px; right: 12px; border-top-color: #F97316; border-right-color: #F97316; border-top-right-radius: 6px; }
        .hww-corner-bl { bottom: 12px; left: 12px; border-bottom-color: #F97316; border-left-color: #F97316; border-bottom-left-radius: 6px; }
        .hww-corner-br { bottom: 12px; right: 12px; border-bottom-color: #F97316; border-right-color: #F97316; border-bottom-right-radius: 6px; }
        
        .hww-card-top {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 20px;
          position: relative;
          z-index: 1;
        }
        .hww-card-num {
          font-family: 'epilogue', sans-serif;
          font-size: clamp(28px, 3vw, 36px);
          font-weight: 800;
          color: #F97316;
          line-height: 1;
        }
        .hww-card-subtitle {
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: #C4A882;
          text-align: right;
          max-width: 70%;
        }
        .hww-card-title {
          font-family: 'epilogue', sans-serif;
          font-size: clamp(20px, 2vw, 24px);
          font-weight: 700;
          color: #1A1208;
          line-height: 1.2;
          margin-bottom: 12px;
          position: relative;
          z-index: 1;
        }
        .hww-card-desc {
          font-size: clamp(13px, 1.5vw, 14px);
          line-height: 1.6;
          color: #6E5E4B;
          margin-bottom: 24px;
          font-weight: 400;
          flex-grow: 1;
          position: relative;
          z-index: 1;
        }
        .hww-card-chips {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          margin-top: auto;
          position: relative;
          z-index: 1;
        }
        .hww-card-chip {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          background: #FDFBF7;
          border: 1px solid #F3ECE2;
          border-radius: 8px;
          font-size: 11px;
          font-weight: 500;
          color: #7A6B58;
        }
        .hww-card-chip .dot {
          width: 5px; height: 5px;
          border-radius: 50%;
          background: #F97316;
        }
        .hww-card-item.cta-card {
          background: #1A1208;
          border-color: #2D2214;
          justify-content: center;
          align-items: center;
          text-align: center;
          color: #FFFFFF;
          box-shadow: 0 25px 60px rgba(18,10,3,0.18);
        }
        .hww-cta-title {
          font-family: 'epilogue', sans-serif;
          font-size: clamp(22px, 2.2vw, 28px);
          font-weight: 700;
          line-height: 1.2;
          margin-bottom: 12px;
        }
        .hww-cta-title em { font-style: italic; color: #F97316; }
        .hww-cta-desc {
          font-size: 13.5px;
          color: #C4A882;
          line-height: 1.5;
          margin-bottom: 24px;
          font-weight: 400;
        }
        .hww-cta-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 14px 28px;
          background: #F97316;
          color: #FFFFFF;
          text-decoration: none;
          border-radius: 10px;
          font-size: 13px;
          font-weight: 600;
          letter-spacing: 0.02em;
          transition: background 0.25s, transform 0.25s;
        }
        .hww-cta-btn:hover { background: #EA580C; transform: translateY(-2px); }
        .hww-floating-img {
          position: absolute;
          bottom: 20px; right: 40px;
          width: clamp(200px, 22vw, 300px);
          height: clamp(200px, 22vw, 300px);
          object-fit: contain;
          pointer-events: none;
          z-index: 10;
          will-change: transform;
        }
        @media (max-width: 1024px) {
          .hww-header { padding: 50px 20px 0px; text-align: center; }
          .hww-header h1 { background-position-x: 0% !important; }
          .hww-fixed-bg-wrapper { position: absolute; height: 100%; }
          .hww-fixed-bg { position: absolute; height: 100%; opacity: 1 !important; }
          .hww-scroll-engine { height: auto; display: block; overflow: visible; }
          .hww-cards-row { grid-template-columns: 1fr; gap: 24px; padding: 40px 24px 60px; }
          .hww-card-item { height: auto; }
          .hww-floating-img { position: relative; bottom: auto; right: auto; margin: 0 auto 60px; display: block; }
        }
        @media (min-width: 1025px) and (max-width: 1200px) {
          .hww-cards-row { padding: 0 24px; gap: 16px; }
        }
      `}</style>

      <section ref={sectionRef} className="hww-wrap">
        <div className="hww-fixed-bg-wrapper">
          <div
            className="hww-fixed-bg"
            style={{ backgroundImage: `url(${BG_IMAGE})` }}
          />
        </div>

        <div className="hww-content-layer">
          <div className="hww-header">
            <h1 ref={headingRef}>OUR WORKING STEPS</h1>
          </div>

          <div ref={triggerContainerRef} className="hww-scroll-engine">
            <div className="hww-cards-row">
              {steps.map((step, index) => (
                <div
                  key={index}
                  ref={(el) => (cardsRef.current[index] = el)}
                  className="hww-card-item"
                >
                  <div className="hww-corner-accent hww-corner-tl" />
                  <div className="hww-corner-accent hww-corner-tr" />
                  <div className="hww-corner-accent hww-corner-bl" />
                  <div className="hww-corner-accent hww-corner-br" />
                  <div className="hww-card-top">
                    <span className="hww-card-num">{step.number}</span>
                    <span className="hww-card-subtitle">{step.subtitle}</span>
                  </div>
                  <h3 className="hww-card-title">{step.title}</h3>
                  <p className="hww-card-desc">{step.description}</p>
                  <div className="hww-card-chips">
                    {step.deliverables.map((d, di) => (
                      <div key={di} className="hww-card-chip">
                        <span className="dot" />
                        {d}
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              <div
                ref={(el) => (cardsRef.current[steps.length] = el)}
                className="hww-card-item cta-card"
              >
                <div className="hww-corner-accent hww-corner-tl" />
                <div className="hww-corner-accent hww-corner-tr" />
                <div className="hww-corner-accent hww-corner-bl" />
                <div className="hww-corner-accent hww-corner-br" />
                <h3 className="hww-cta-title">Ready to Build<br /><em>Your Store?</em></h3>
                <p className="hww-cta-desc">Let's convert your multi-channel roadmap into automated revenue.</p>
                <a href="#contact" className="hww-cta-btn">Let's Work Together</a>
              </div>
            </div>

            <img
              ref={floatingImageRef}
              src={FLOAT_IMG}
              alt="Decorative graphic"
              className="hww-floating-img"
              width={300}
              height={300}
              loading="lazy"
              decoding="async"
            />
          </div>
        </div>
      </section>
    </>
  );
}